import {LitElement, html, css, type CSSResultGroup} from 'lit';
import {SignalWatcher} from '@lit-labs/preact-signals';
import {env} from '../../config/env.js';
import {authedFetch} from '../../auth/auth-fetch.js';
import {morphedRead, morphedCreate} from '../../services/morphed-entity.service.js';

interface CognitoUser {username: string; email: string; sub: string; enabled: boolean; status: string; name?: string; family_name?: string}
interface Org {id: string; name: string; displayName?: string}
interface MorphedUserLite {id: string; email?: string; name?: string; organizations?: string[]}
interface Toast {kind: 'ok' | 'err'; text: string}

export class ClamCognitoImporter extends SignalWatcher(LitElement) {
    static styles: CSSResultGroup = css`
        :host { display: block; font-family: var(--clam-font); color: var(--clam-text); }
        .toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; padding: var(--clam-space-4, 16px); gap: var(--clam-space-3, 12px); }
        .toolbar__left { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .toolbar__title { font-size: var(--clam-text-xl, 20px); font-weight: 700; color: var(--clam-darkgray, #424244); margin: 0; }
        .toolbar__subtitle { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 0; }
        .toolbar__right { display: flex; align-items: center; gap: var(--clam-space-2, 8px); flex-wrap: wrap; }
        .org-select { padding: 8px 10px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); font-size: var(--clam-text-sm, 13px); font-family: var(--clam-font); background: var(--clam-surface, #fff); color: var(--clam-text, #333); }
        .org-select:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }

        .btn { padding: 7px var(--clam-space-4, 16px); border-radius: var(--clam-radius-sm, 6px); border: none; cursor: pointer; font-size: var(--clam-text-sm, 13px); font-weight: 500; font-family: var(--clam-font); white-space: nowrap; }
        .btn-primary { background: var(--clam-light-green, #6bbb24); color: var(--clam-white, #fff); }
        .btn-primary:hover { background: var(--clam-green, #5a9d1e); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { background: var(--clam-surface, #fff); color: var(--clam-text, #333); border: 1px solid var(--clam-border, #e2e8f0); }
        .btn-secondary:hover { background: var(--clam-surface-soft, #f5f6f8); }
        .btn-sm { padding: 6px 12px; font-size: var(--clam-text-xs, 11px); min-height: 34px; }

        .cards-grid { display: grid; grid-template-columns: 1fr; gap: var(--clam-space-3, 12px); padding: 0 var(--clam-space-4, 16px) var(--clam-space-5, 20px); align-items: start; }
        .card { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-lg, 12px); background: var(--clam-surface, #fff); box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.05)); display: flex; flex-direction: column; overflow: hidden; }
        .card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--clam-space-2, 8px); padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px); }
        .card__title { font-size: var(--clam-text-base, 14px); font-weight: 700; color: var(--clam-text, #333); margin: 0; word-break: break-word; }
        .card__sub { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 3px 0 0; word-break: break-all; }
        .card__body { padding: 0 var(--clam-space-4, 16px) var(--clam-space-2, 8px); display: flex; gap: var(--clam-space-2, 8px); flex-wrap: wrap; align-items: center; }
        .card__foot { display: flex; gap: var(--clam-space-2, 8px); padding: var(--clam-space-2, 8px) var(--clam-space-4, 16px) var(--clam-space-3, 12px); border-top: 1px solid var(--clam-border, #e2e8f0); flex-wrap: wrap; align-items: center; }

        .badge { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 2px 8px; border-radius: 999px; display: inline-flex; align-items: center; gap: 4px; }
        .badge--on { background: #dcfce7; color: #166534; }
        .badge--off { background: #fee2e2; color: #b91c1c; }
        .badge--warn { background: #fef3c7; color: #92400e; }
        .badge--linked { background: #dcfce7; color: #166534; }
        .badge--unlinked { background: #fef3c7; color: #92400e; }
        .org-chip { background: #e8f0fe; color: #1e40af; border-radius: 999px; padding: 2px 9px; font-size: var(--clam-text-xs, 11px); }
        .muted { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); }
        .loading { text-align: center; padding: var(--clam-space-8, 40px); color: var(--clam-light-gray, #898a8c); display: flex; align-items: center; justify-content: center; gap: 8px; }
        .spinner { display: inline-block; width: 15px; height: 15px; border: 2px solid var(--clam-border, #e2e8f0); border-top-color: var(--clam-primary, #5a9d1e); border-radius: 50%; animation: cci-spin 0.7s linear infinite; }
        @keyframes cci-spin { to { transform: rotate(360deg); } }
        .toast { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 300; padding: 12px 20px; border-radius: var(--clam-radius-md, 8px); font-size: var(--clam-text-sm, 13px); font-weight: 600; box-shadow: 0 6px 20px rgba(0,0,0,0.18); max-width: 90vw; }
        .toast--ok { background: #dcfce7; color: #3f7a12; }
        .toast--err { background: #fee2e2; color: #b91c1c; }
        @media (min-width: 640px) {
            .toolbar { padding: var(--clam-space-5, 20px) var(--clam-space-6, 24px); }
            .cards-grid { grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); padding: 0 var(--clam-space-6, 24px) var(--clam-space-6, 24px); gap: var(--clam-space-4, 16px); }
        }
    `;

    private _cognito: CognitoUser[] = [];
    private _morphed = new Map<string, MorphedUserLite>();
    private _orgs: Org[] = [];
    private _selectedOrgId = '';
    private _loading = false;
    private _linkingSub = '';
    private _toast: Toast | null = null;

    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await this._loadAll();
    }

    private _toast_(kind: Toast['kind'], text: string): void {
        this._toast = {kind, text};
        this.requestUpdate();
        setTimeout(() => { this._toast = null; this.requestUpdate(); }, 3200);
    }

    private async _loadAll(): Promise<void> {
        this._loading = true; this.requestUpdate();
        try {
            const [cRes, morphedUsers, orgs] = await Promise.all([
                authedFetch(`${env.MORPHED_API_URL}/auth/users?limit=60`, {audience: 'morphed'}),
                morphedRead<MorphedUserLite>('morphed-user', {filters: [], options: {limit: 200}}),
                morphedRead<Org>('morphed-organization', {filters: [], options: {limit: 50}}),
            ]);
            this._morphed = new Map((morphedUsers || []).map((u) => [u.id, u]));
            this._orgs = orgs || [];
            if (!this._selectedOrgId) {
                const cur = this._orgs.find((o) => o.name === env.MORPHED_ORG);
                this._selectedOrgId = cur?.id || this._orgs[0]?.id || '';
            }
            if (!cRes.ok) throw new Error(`HTTP ${cRes.status}`);
            const data = await cRes.json();
            this._cognito = (data.users || []) as CognitoUser[];
        } catch (e) {
            this._toast_('err', `Error cargando: ${String(e)}`);
            this._cognito = [];
        } finally {
            this._loading = false; this.requestUpdate();
        }
    }

    private async _create(u: CognitoUser): Promise<void> {
        if (!this._selectedOrgId) { this._toast_('err', 'Selecciona una organización destino.'); return; }
        this._linkingSub = u.sub; this.requestUpdate();
        try {
            await morphedCreate('morphed-user', [{
                id: u.sub, email: u.email || '', name: u.name || u.email || u.username, family_name: u.family_name || '',
                role: 'agent', status: 'invited', authProvider: 'cognito', authProviderId: u.sub, organizations: [this._selectedOrgId],
            }]);
            this._morphed.set(u.sub, {id: u.sub, email: u.email, organizations: [this._selectedOrgId]});
            this._toast_('ok', `${u.email || u.username} creado en ${this._orgLabel(this._selectedOrgId)}.`);
        } catch (e) {
            this._toast_('err', `Error: ${String(e)}`);
        } finally {
            this._linkingSub = ''; this.requestUpdate();
        }
    }

    private _orgLabel(id: string): string {
        return this._orgs.find((o) => o.id === id)?.name || id;
    }
    private _orgName(id: string): string {
        return this._orgs.find((o) => o.id === id)?.name || '?';
    }

    render() {
        const linked = this._cognito.filter((u) => this._morphed.has(u.sub));
        const unlinked = this._cognito.filter((u) => !this._morphed.has(u.sub));
        return html`
            <div class="toolbar">
                <div class="toolbar__left">
                    <h1 class="toolbar__title">Importar desde Cognito</h1>
                    <p class="toolbar__subtitle">Todos los usuarios del pool · ${linked.length} en Morphed · ${unlinked.length} sin Morphed</p>
                </div>
                <div class="toolbar__right">
                    <span class="muted" style="font-weight:600">Org destino al crear:</span>
                    <select class="org-select" .value=${this._selectedOrgId} @change=${(e: Event) => { this._selectedOrgId = (e.target as HTMLSelectElement).value; this.requestUpdate(); }}>
                        ${this._orgs.map((o) => html`<option value=${o.id} ?selected=${o.id === this._selectedOrgId}>${o.displayName || o.name}</option>`)}
                    </select>
                    <button class="btn btn-secondary" @click=${() => this._loadAll()} ?disabled=${this._loading}>${this._loading ? html`<span class="spinner"></span>` : ''} Refrescar</button>
                </div>
            </div>

            ${this._loading && this._cognito.length === 0
                ? html`<div class="loading"><span class="spinner"></span> Cargando usuarios de Cognito...</div>`
                : this._cognito.length === 0
                    ? html`<div class="muted" style="padding:40px;text-align:center">No se pudieron cargar usuarios (¿sin permiso de admin?).</div>`
                    : html`<div class="cards-grid">${this._cognito.map((u) => this._renderCard(u))}</div>`}

            ${this._toast ? html`<div class="toast toast--${this._toast.kind}">${this._toast.text}</div>` : ''}
        `;
    }

    private _renderCard(u: CognitoUser) {
        const mu = this._morphed.get(u.sub);
        const hasMorphed = !!mu;
        const orgs = mu?.organizations || [];
        const inSelected = hasMorphed && this._selectedOrgId && orgs.includes(this._selectedOrgId);
        return html`
            <div class="card">
                <div class="card__head">
                    <div>
                        <h3 class="card__title">${u.email || u.username}</h3>
                        <p class="card__sub">${u.username} · sub ${u.sub.slice(0, 8)}…</p>
                    </div>
                    <div style="display:flex;flex-direction:column;gap:4px;align-items:flex-end">
                        ${hasMorphed ? html`<span class="badge badge--linked">en morphed</span>` : html`<span class="badge badge--unlinked">sin morphed</span>`}
                        ${u.enabled === false ? html`<span class="badge badge--off">deshab.</span>` : ''}
                    </div>
                </div>
                <div class="card__body">
                    ${hasMorphed
                        ? (orgs.length ? orgs.map((oid) => html`<span class="org-chip">${this._orgName(oid)}</span>`) : html`<span class="muted">sin organización</span>`)
                        : html`<span class="muted">no tiene registro morphed_user</span>`}
                </div>
                <div class="card__foot">
                    ${!hasMorphed
                        ? html`<button class="btn btn-primary btn-sm" ?disabled=${this._linkingSub === u.sub || !this._selectedOrgId} @click=${() => this._create(u)}>
                            ${this._linkingSub === u.sub ? html`<span class="spinner"></span>` : html`+ Crear en ${this._orgLabel(this._selectedOrgId)}`}
                          </button>`
                        : inSelected
                            ? html`<span class="muted">Ya está en ${this._orgLabel(this._selectedOrgId)} ✓</span>`
                            : html`<span class="muted">Para reasignar org, edita desde “Usuarios Morphed”</span>`}
                </div>
            </div>
        `;
    }
}

customElements.define('clam-cognito-importer', ClamCognitoImporter);

declare global {
    interface HTMLElementTagNameMap {
        'clam-cognito-importer': ClamCognitoImporter;
    }
}
